import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { EnterpriseFooter } from "@/pages/enterprise/Enterprise";
import { ArrowRight, Mail, MapPin } from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-lg tracking-tight">
            Rule<span className="text-primary">AI</span><span className="text-muted-foreground">.systems</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/enterprise" className="hover:text-primary">Enterprise</Link>
            <Link to="/security" className="hover:text-primary">Security</Link>
            <Link to="/library" className="hover:text-primary">Library</Link>
            <Link to="/about" className="text-primary font-medium">Company</Link>
            <Link to="/contact"><Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90">Contact</Button></Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="overline text-primary mb-4">/ COMPANY</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-tight" data-testid="about-hero">
            Built by operators.<br/>
            For operators.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-3xl">
            RuleAI.Systems was founded on a plain observation: businesses don&rsquo;t have a people problem, they have a systems problem. We build the operating system we spent years wishing existed.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="border-b border-border bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="overline text-primary mb-3">/ OUR MISSION</div>
              <h2 className="font-display font-bold text-3xl tracking-tight">
                Give every business the operational intelligence once reserved for the world&rsquo;s largest enterprises.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Every business already has the potential to become exceptional. Most simply need systems that allow that potential to scale.
              </p>
              <p>
                We&rsquo;re not here to replace people. We&rsquo;re here to amplify them. RuleAI captures institutional knowledge, standardizes execution, connects teams, and drives continuous improvement across the entire organization.
              </p>
              <p>
                Everything we build serves one mission: turn knowledge into execution, and execution into competitive advantage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="overline text-primary mb-3">/ FOUNDERS</div>
          <h2 className="font-display font-bold text-3xl tracking-tight mb-10">
            Two operators &mdash; not two coders.
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-border bg-card p-6 rounded-sm">
              <div className="aspect-square w-32 rounded-full overflow-hidden bg-secondary border border-border">
                <img src="/founder/scott-poore-jr.webp" alt="Scott Poore Jr." className="w-full h-full object-cover" />
              </div>
              <div className="mt-5">
                <div className="font-display font-bold text-2xl tracking-tight">Scott Poore Jr.</div>
                <div className="overline text-muted-foreground mt-1">Founder &amp; CEO</div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                Scott spent years inside manufacturing operations — leading NCR investigations and 8D problem-solving sessions, preparing for ISO audits, standing up OEE measurement, authoring standard work, and chasing operator SOP compliance on real production lines. He watched talented teams held back the same way every time: the systems around them couldn&rsquo;t keep up. RuleAI is the operating system he spent years wishing existed, built with the discipline the shop floor demands.
              </p>
            </div>
            <div className="border border-border bg-card p-6 rounded-sm">
              <div className="aspect-square w-32 rounded-full overflow-hidden bg-secondary border border-border">
                <img src="/founder/heather-eccleston.jpg" alt="Heather Nicole Eccleston" className="w-full h-full object-cover" />
              </div>
              <div className="mt-5">
                <div className="font-display font-bold text-2xl tracking-tight">Heather Nicole Eccleston</div>
                <div className="overline text-muted-foreground mt-1">Co-founder</div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                Heather partners on go-to-market, customer success, and product strategy &mdash; translating direct customer feedback into an enterprise-ready platform that operators actually adopt.
              </p>
            </div>
          </div>
          <div className="mt-10 text-center">
            <Link to="/#founder" className="text-sm text-primary hover:underline font-mono-plex uppercase tracking-widest inline-flex items-center gap-2">
              Read the full founders&rsquo; note <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-b border-border bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="overline text-primary mb-3">/ HOW WE OPERATE</div>
          <h2 className="font-display font-bold text-3xl tracking-tight mb-10 max-w-3xl">
            The principles that guide how we build, sell, and support.
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { t: "Honesty over spin", d: "No fake customer logos. No made-up testimonials. When we&rsquo;re in beta, we say we&rsquo;re in beta." },
              { t: "Depth over breadth", d: "One customer&rsquo;s vertical solved cold beats twenty industries served shallowly. We go deep." },
              { t: "Systems over heroics", d: "If a customer needs a hero to make our product work, we&rsquo;ve built the wrong thing. Systems make excellence repeatable." },
              { t: "Founder-signed answers", d: "Every ticket, every submission, every enterprise call &mdash; a founder replies personally, at least until we scale past 500 customers." },
              { t: "Long time horizon", d: "We&rsquo;re building a business that outlasts us. Optimizing for the compounding decade, not the quarter." },
              { t: "The customer&rsquo;s data is theirs", d: "Full export, no vendor lock-in, no training AI on customer content. Ever." },
            ].map((v) => (
              <div key={v.t} className="border border-border bg-card p-6 rounded-sm">
                <div className="font-display font-bold text-lg">{v.t}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company facts */}
      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <div className="overline text-primary mb-3">/ COMPANY FACTS</div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <FactBox label="Founded" value="2026" />
            <FactBox label="Headquartered" value="Pennsylvania, United States" />
            <FactBox label="Employees" value="Founding team (2)" />
            <FactBox label="Stage" value="Pre-seed · Private beta" />
            <FactBox label="Product" value="AI-native Business OS · SaaS" />
            <FactBox label="Focus" value="Mid-market manufacturing &amp; industrial operations" />
          </div>
          <div className="mt-8 border border-border bg-card p-6 rounded-sm">
            <div className="overline text-primary mb-3">Contact</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>General: <a href="mailto:hello@ruleai.systems" className="text-primary hover:underline">hello@ruleai.systems</a></span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>Support: <a href="mailto:support@ruleai.systems" className="text-primary hover:underline">support@ruleai.systems</a></span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>Security: <a href="mailto:security@ruleai.systems" className="text-primary hover:underline">security@ruleai.systems</a></span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>Investors: <a href="mailto:invest@ruleai.systems" className="text-primary hover:underline">invest@ruleai.systems</a></span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span>Pennsylvania, United States</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <EnterpriseFooter />
    </div>
  );
}

function FactBox({ label, value }) {
  return (
    <div className="border border-border bg-card p-5 rounded-sm">
      <div className="overline text-muted-foreground">{label}</div>
      <div className="font-medium mt-1.5" dangerouslySetInnerHTML={{ __html: value }} />
    </div>
  );
}
